import React, { useState, useRef, useEffect } from "react";
import Popupz from "../atoms/Popupz";
import Inputz from "../atoms/Inputz";
import Buttonz from "../atoms/Buttonz";
import PropTypes from "prop-types";

import ItemCard from "./ItemCard";
import { addItem, editItem } from "../../services/AdminService";
import { useDispatch } from "react-redux";
import { validationPopup } from "../../redux/actions";
import { BACKEND_URL } from "../../config";

// Import utility functions
import {
  validateItemForm,
  createPreviewData,
  getCategoryOptions,
  getPlatformOptions,
  getPriceOptions,
} from "../../utils/itemFormUtils";

// Import form components
import CategorySelector from "./ItemForm/CategorySelector";
import PlatformSelector from "./ItemForm/PlatformSelector";
import PriceSelector from "./ItemForm/PriceSelector";
import ImageUploader from "./ItemForm/ImageUploader";
import DescriptionFields from "./ItemForm/DescriptionFields";
import TagSelector from "./ItemForm/TagSelector";
import { data } from "../../data/itemsData";

const AddItemDashboardPopup = ({ onClose, onSave, itemToEdit }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: "",
    engDescription: "",
    jpDescription: "",
    link: "",
    category: "tools",
    platform: [],
    price: "free",
    tags: "",
    filterTag: [],
  });

  const [image, setImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Get options from utility functions
  const categories = getCategoryOptions();
  const platforms = getPlatformOptions(formData.category);
  const priceOptions = getPriceOptions();

  // Initialize form with item data if editing
  useEffect(() => {
    if (itemToEdit) {
      const category = itemToEdit.category_id[0]?.name || "tools";
      const lowercaseArray =
        itemToEdit?.platform &&
        itemToEdit?.platform?.map((item) => item.toLowerCase());
      setFormData({
        ...itemToEdit,
        _id: itemToEdit._id,
        category: category,
        platform: lowercaseArray,
        price: itemToEdit.price || "free",
        filterTag: itemToEdit.filterTag || [],
        engDescription: itemToEdit.description?.[0] || "",
        jpDescription: itemToEdit.description?.[1] || "",
        linkAndroid: itemToEdit.linkAndroid || "",
        linkWindows: itemToEdit.linkWindows || "",
        tags: itemToEdit.tags.join(",") || "",
      });

      // Set image URL for preview if item has an image
      if (itemToEdit.img) {
        const fullImageUrl = `${BACKEND_URL}/images/items/${itemToEdit.img}`;
        setImageUrl(fullImageUrl);
        // Clear any previously selected new image
        setImage(null);
      }
    }
  }, [itemToEdit]);

  // Create preview data
  const previewData = createPreviewData(formData, image, imageUrl);

  // Get filter tags from data
  const filterTags = {
    resos: ["resos", "asdf"],
    tools: ["tools", "asdf"],
    ideaz: ["ideaz", "asdf"],
    apps: ["apps", "df"],
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target ? e.target : e;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const handleCategoryChange = (category) => {
    setFormData({
      ...formData,
      category,
    });
  };

  const handlePriceChange = (price) => {
    setFormData({
      ...formData,
      price,
    });
  };

  const handlePlatformToggle = (platform) => {
    const updatedPlatforms = formData.platform.includes(platform)
      ? formData.platform.filter((p) => p !== platform)
      : [...formData.platform, platform];

    setFormData({
      ...formData,
      platform: updatedPlatforms,
    });
  };

  const handleTagToggle = (tag) => {
    const updatedTags = formData.filterTag.includes(tag)
      ? formData.filterTag.filter((t) => t !== tag)
      : [...formData.filterTag, tag];

    setFormData({
      ...formData,
      filterTag: updatedTags,
    });
  };

  const handleImageChange = (file) => {
    setImage(file);
    if (file) {
      setImageUrl(URL.createObjectURL(file));
    }

    // Clear image error if it exists
    if (errors.image) {
      setErrors({
        ...errors,
        image: null,
      });
    }
  };

  const handleSubmit = async () => {
    // Validate form
    const newErrors = validateItemForm(formData, image, !itemToEdit);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Show validation error popup
      dispatch(
        validationPopup({
          message: "Please fill in all required fields correctly",
          icon: "horror",
          type: "error",
        })
      );
      return;
    }

    try {
      setIsSubmitting(true);

      // Show loading popup
      dispatch(
        validationPopup({
          message: itemToEdit ? "Updating item..." : "Adding new item...",
          icon: "loading_ani",
        })
      );

      let response;

      if (itemToEdit) {
        // Edit existing item
        response = await editItem(formData, imageUrl ? imageUrl : null);
      } else {
        // Add new item
        const imageUrlToSend = image ? URL.createObjectURL(image) : null;
        response = await addItem(formData, imageUrlToSend);
      }

      // Show success popup
      dispatch(
        validationPopup({
          message: itemToEdit
            ? "Item updated successfully!"
            : "Item added successfully!",
          icon: "like",
          type: "success",
        })
      );

      // Call the on Save callback with the response data
      setTimeout(() => {
        window.location.reload();
      }, 100);
      onClose();
    } catch (error) {
      console.error(
        itemToEdit ? "Error updating item:" : "Error adding item:",
        error
      );

      // Show error popup
      dispatch(
        validationPopup({
          message: itemToEdit
            ? "Failed to update item. Please try again."
            : "Failed to add item. Please try again.",
          icon: "horror",
          type: "error",
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popupz
      title={itemToEdit ? "Edit Item" : "Add New Item"}
      onClose={onClose}
      popupId="add-item"
      className="add-item-popup"
    >
      <div className="popup-layout">
        <div className="preview-column">
          <div className="item-card-preview">
            <ItemCard
              data={previewData}
              selectedMode={formData.category}
              page="dashboard"
              demo={true}
            />
          </div>
        </div>

        {/* Form Column */}
        <div className="form-column">
          {/* {itemToEdit ? "Edit Item" : "Add New Item"} */}

          <div className="form-box">
            <div className="info-selector">
              {/* Image Upload */}
              <ImageUploader
                image={image}
                imageUrl={imageUrl}
                onChange={handleImageChange}
                error={errors.image}
                fileInputRef={fileInputRef}
                required={!itemToEdit}
              />
              {/* Category Selection */}
              <CategorySelector
                categories={categories}
                selectedCategory={formData.category}
                onChange={handleCategoryChange}
              />
              {/* Platform Selection - Only show for Tools category */}
              {(formData.category === "tools" || "apps") && (
                <PlatformSelector
                  category={formData.category}
                  platforms={platforms}
                  selectedPlatforms={formData.platform}
                  onChange={handlePlatformToggle}
                  error={errors.platform}
                />
              )}
              {/* Price Selection - Only show for Tools category */}
              {formData.category === "tools" && (
                <PriceSelector
                  priceOptions={priceOptions}
                  selectedPrice={formData.price}
                  onChange={handlePriceChange}
                />
              )}
            </div>
            {/* Name Input */}
            <Inputz
              iconL="edit"
              focusL="edit"
              label="Name *"
              type="text"
              placeholder="Item Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              error={errors.name}
            />
            {formData.category !== "apps" && (
              <Inputz
                iconL="view_link"
                focusL="link"
                label="Link *"
                type="url"
                placeholder="Item URL"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                error={errors.link}
              />
            )}
            {/* Description Fields */}
            <DescriptionFields
              engDescription={formData.engDescription}
              jpDescription={formData.jpDescription}
              onChange={handleInputChange}
              errors={errors}
              required={true}
            />
            {/* Link Input */}{" "}
            {formData.category === "apps" && (
              <>
                {formData?.platform?.includes("android") && (
                  <Inputz
                    iconL="android"
                    focusL="android"
                    label="Android Link"
                    type="url"
                    placeholder="Android App URL"
                    name="linkAndroid"
                    value={formData?.linkAndroid || ""}
                    onChange={handleInputChange}
                  />
                )}
                {formData?.platform?.includes("apple") && (
                  <Inputz
                    iconL="apple"
                    focusL="apple"
                    label="apple Link"
                    type="url"
                    placeholder="apple App URL"
                    name="link"
                    value={formData.link || ""}
                    onChange={handleInputChange}
                  />
                )}
              </>
            )}
            {/* <label className="form-label">Selected Tags</label> */}
            <Inputz
              iconL="tag"
              focusL="tags"
              label="selectedtags "
              type="url"
              placeholder="selectedtags "
              name="tags"
              value={formData.tags || ""}
              onChange={handleInputChange}
            />{" "}
            <TagSelector
              availableTags={data[formData?.category].tags}
              selectedTags={formData.filterTag}
              onToggle={handleTagToggle}
              error={errors.filterTag}
            />
            <div className="selected-tags"></div>
            {/* Action Buttons */}
            <div className="popup-actions">
              <Buttonz
                label="Cancel"
                onClick={onClose}
                className="cancel-button"
              />
              <Buttonz
                label={itemToEdit ? "Update" : "Save"}
                onClick={handleSubmit}
                className="save-button"
                iconR="save"
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
      </div>
    </Popupz>
  );
};

AddItemDashboardPopup.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  itemToEdit: PropTypes.object,
};

export default AddItemDashboardPopup;
